module Client.Components.Graph.WeatherStation

open System
open Client.Components.Graph.Recharts
open Client.Components.GraphCommon
open Client.Domain
open Feliz.Recharts
open Fulma
open Shared
open Shared.Dto.Dto
open Shared.Dto.SensorGraphData
open Shared.DtoTypes.MapSensorData
open Shared.Infrastructure

type WeatherStationGraphNode = {
    Ticks: int
    Temperature: float
    HumidTemperature: float
    DewPoint: float
    AirHumidity: float
    LeafletWetness: int
    MaxWindSpeed: float
    AverageWindSpeed: float
    IsForecast: bool
}

type WeatherStationGraphData = {
    TemperatureAxis: float list
    HumidityAxis: float list
    WindSpeedAxis: float list
    Data: WeatherStationGraphNode list
    CurrentTimestamp: DateTimeOffset
}

let private createTemperatureAxisTicks (data: WeatherStationGraphNode list) =
    let minValue =
        data
        |> List.map (fun graphNode ->
            [
                graphNode.Temperature
                graphNode.HumidTemperature
                graphNode.DewPoint
            ]
            |> List.min
        )
        |> List.min

    let maxValue =
        data
        |> List.map (fun graphNode ->
            [
                graphNode.Temperature
                graphNode.HumidTemperature
                graphNode.DewPoint
            ]
            |> List.max
        )
        |> List.max

    AxisTicks.generate false 5. minValue maxValue

let private createWindSpeedAxisTicks (data: WeatherStationGraphNode list) =
    let minValue =
        data
        |> List.map (fun graphNode ->
            [
                graphNode.MaxWindSpeed
                graphNode.AverageWindSpeed
            ]
            |> List.min
        )
        |> List.min

    let maxValue =
        data
        |> List.map (fun graphNode ->
            [
                graphNode.MaxWindSpeed
                graphNode.AverageWindSpeed
            ]
            |> List.max
        )
        |> List.max

    AxisTicks.generate true 1. minValue maxValue

let private createWeatherStationGraphData (data: WeatherStationGraphNode list) =
    let temperatureAxisTicks = createTemperatureAxisTicks data
    let windSpeedAxisTicks = createWindSpeedAxisTicks data
    let humidityAxis = [ 0. .. 20. .. 100. ]

    {
        TemperatureAxis = temperatureAxisTicks
        HumidityAxis = humidityAxis
        WindSpeedAxis = windSpeedAxisTicks
        Data = data
        CurrentTimestamp = DateTimeOffset.Now
    }

let private toWeatherGraphData (data: GraphWeatherStationDataDto) = {
    Ticks = timestampToTicks data.TimeStamp
    Temperature = data.AirTemperature
    HumidTemperature = Math.Round(data.HumidTemperature, 2)
    DewPoint = Math.Round(data.DewPoint, 2)
    AirHumidity = data.AirHumidity
    LeafletWetness = leafletWetnessToInt data.LeafletWetness
    AverageWindSpeed = data.AverageWindSpeed
    MaxWindSpeed = data.MaxWindSpeed
    IsForecast = data.IsForecast
}

let mapDataToWeatherStation (data: GraphDataDto) =
    match data with
    | WeatherStation weatherStationData -> Some weatherStationData
    | _ -> None

let graphDataDtoToWeatherStationGraphData data =
    data |> List.map toWeatherGraphData |> createWeatherStationGraphData

let currentWeatherStationDataBox (data: WeatherStationSensorData) = [
    Level.level [] [
        currentDataLevelItem "Wann?" (DateTime.toShortString data.Date.LocalDateTime)
        currentDataLevelItem "Akt. Temperatur" (MeasuredValueString.temperatureToString data.AirTemperature)
        currentDataLevelItem "Akt. Luftfeuchtigkeit" (MeasuredValueString.percentageToString data.AirHumidity)
        currentDataLevelItem
            "Min. Temperatur"
            (DatedValue.toString MeasuredValueString.temperatureToString data.MinTemperature)
        currentDataLevelItem
            "Max. Temperatur"
            (DatedValue.toString MeasuredValueString.temperatureToString data.MaxTemperature)
        currentDataLevelItem
            "Max. Windgeschwindigkeit"
            (data.MaxWindSpeed
             |> Option.map (DatedValue.toString MeasuredValueString.windSpeedToString)
             |> Option.defaultValue "-")
        currentDataLevelItem
            "Durchschn. Windgeschwindigkeit"
            (data.AverageWindSpeed
             |> Option.map MeasuredValueString.windSpeedToString
             |> Option.defaultValue "-")
    ]
]

let weatherStationDataGraphs (graphData: WeatherStationGraphData) =
    let temperatureTicks = AxisTicks.createYProperty graphData.TemperatureAxis

    let xDomain = xAxis.domain (domain.min, domain.max)

    let yDomain = yAxis.domain (domain.min, domain.max)

    let xAxisTickInterval = xAxis.interval.preserveStartEnd

    let dataKey = (fun (p: WeatherStationGraphNode) -> p.Ticks) |> xAxis.dataKey

    let forecastLine = forecastLineProps graphData.CurrentTimestamp

    let temperatureChart =
        Recharts.lineChart [
            lineChart.syncId "syncId1"
            lineChart.data graphData.Data
            lineChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                    xAxisTickInterval
                ]
                Recharts.yAxis [ temperatureTicks; yAxis.unit " °C"; yDomain ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContentWithForecast ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.Temperature)
                    line.name "Temperatur [°C]"
                    line.stroke "#FFC000"
                ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.HumidTemperature)
                    line.name "Feuchttemperatur [°C]"
                    line.stroke "#7030A0"
                ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.DewPoint)
                    line.name "Taupunkt [°C]"
                    line.stroke "#0071C1"
                ]

                Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]

            //Recharts.referenceLine forecastLine
            ]
        ]
        |> fullHeightGraphBox "Verlauf der Temperatur"

    let humidityYAxis = AxisTicks.createYProperty graphData.HumidityAxis

    let humidityChart =
        Recharts.lineChart [
            lineChart.syncId "syncId1"
            lineChart.data graphData.Data
            lineChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                ]
                Recharts.yAxis [ yAxis.unit "%"; humidityYAxis ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContentWithForecast ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun p -> p.AirHumidity)
                    line.name "Luftfeuchtigkeit [%]"
                ]

                Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]

            //Recharts.referenceLine forecastLine
            ]
        ]
        |> fullHeightGraphBox "Verlauf der Luftfeuchtigkeit"

    let wetnessTicks = AxisTicks.createYProperty [ 0; 1 ]

    let leafletWetnessGraph =
        Recharts.areaChart [
            areaChart.syncId "syncId1"
            areaChart.data graphData.Data
            areaChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                ]
                Recharts.yAxis [
                    yAxis.dataKey (fun (p: WeatherStationGraphNode) -> p.LeafletWetness)
                    yAxis.number
                    wetnessTicks
                ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContentWithForecast ]
                Recharts.area [
                    area.fillOpacity 0.2
                    area.dataKey (fun (p: WeatherStationGraphNode) -> p.LeafletWetness)
                    area.name "Blattnässe"
                ]

            //Recharts.referenceLine forecastLine
            ]
        ]
        |> halfHeightGraphBox "Verlauf der Blattnässe"

    let windSpeedTicks = AxisTicks.createYProperty graphData.WindSpeedAxis

    let windSpeedChart =
        Recharts.lineChart [
            lineChart.syncId "syncId1"
            lineChart.data graphData.Data
            lineChart.children [
                Recharts.xAxis [
                    dataKey
                    xAxis.number
                    xAxis.scale.time
                    xAxis.tickFormatter formatTimeTick
                    xDomain
                    xAxisTickInterval
                ]
                Recharts.yAxis [ windSpeedTicks; yAxis.unit " km/h"; yDomain ]
                Recharts.legend []
                Recharts.tooltip [ tooltip.content tooltipContentWithForecast ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun (p: WeatherStationGraphNode) -> p.AverageWindSpeed)
                    line.name "Durchschnittliche Windgeschwindigkeit [km/h]"
                    line.stroke "#FFC000"
                ]
                Recharts.line [
                    line.dot false
                    line.dataKey (fun (p: WeatherStationGraphNode) -> p.MaxWindSpeed)
                    line.name "Max. Windgeschwindigkeit [km/h]"
                    line.stroke "#7030A0"
                ]

                Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]

            //Recharts.referenceLine forecastLine
            ]
        ]
        |> fullHeightGraphBox "Verlauf der Windgeschwindigkeit"

    [
        temperatureChart
        humidityChart
        leafletWetnessGraph
        windSpeedChart
    ]